import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { RHero, LyricSong, QuoteBand, RTakeItFurther } from '../components/Resources.jsx';

// Anthem MP3s are served from media.<env>.saysomethingin.com.
// `VITE_MEDIA_BASE` is set per-environment by the deploy workflows.
const MEDIA_BASE = import.meta.env.VITE_MEDIA_BASE || '';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  rCrumb: 'Hen Wlad Fy Nhadau',
  rKicker: 'Mini course · Free',
  rTitle: 'Hen Wlad Fy Nhadau.',
  rLede: 'From the banks of the Rhondda to international sports stadiums – the Welsh national anthem that, one quiet winter morning in 1856, would go on to inspire the rest of the world to sing before a game. Learn it with us in four free lessons, the SSi way.',

  promiseKicker: "What you'll come away with",
  promiseTitle: 'Four short lessons, and the anthem is yours.',
  promise1Title: 'Master the first verse and chorus.',
  promise1Body: "By the end of the fourth lesson, you'll be able to sing the verse the whole stadium sings – with understanding, not just the right sounds. The meaning will sit inside the words for you.",
  promise2Title: 'Make the words part of you.',
  promise2Body: "SSi doesn't ask you to memorise. It asks you to speak. The words bed in so deeply that they come back at the match, at the gathering, at the kitchen sink – because they're yours now.",
  promise3Title: 'See your brain do something remarkable.',
  promise3Body: "A radical new way of learning, and a reminder of what your brain is actually capable of. Most people arrive sceptical. An hour in, they're singing Hen Wlad Fy Nhadau in Welsh.",

  lessonsKicker: 'The course',
  lessonsTitle: 'Four lessons. Press play.',
  lessonsLede: "Each lesson is short enough to slot into a commute or a dog walk. Find somewhere you can speak out loud, say the words along with us, and don't worry about mistakes – mistakes are how it works.",
  l1Num: '01',
  l1Badge: 'Lesson 1',
  l1Title: 'The opening lines.',
  l1Body: "We start with the opening of the first verse – the phrase the whole crowd hums along to even when they don't know the rest. Say each bit out loud before we do. Odd for ninety seconds, normal after that.",
  l1Src: `${MEDIA_BASE}/welsh/anthem/lesson1.mp3`,
  l2Num: '02',
  l2Badge: 'Lesson 2',
  l2Title: 'The rest of the verse.',
  l2Body: "Lesson 2 extends what you have into the full first verse – the brave warriors, the patriots, the blood shed for freedom. We review as we go, so if yesterday feels fuzzy, that's fine.",
  l2Src: `${MEDIA_BASE}/welsh/anthem/lesson2.mp3`,
  l3Num: '03',
  l3Badge: 'Lesson 3',
  l3Title: 'Into the chorus.',
  l3Body: 'The chorus is the bit the whole of Wales knows by heart – Gwlad! Gwlad! In lesson 3 we work through it slowly so you can join in at full volume by the end.',
  l3Src: `${MEDIA_BASE}/welsh/anthem/lesson3.mp3`,
  l4Num: '04',
  l4Badge: 'Lesson 4',
  l4Title: 'Pulling it all together.',
  l4Body: 'In the final lesson we weave the verse and the chorus back into one, tidy the tricky corners, and send you off ready to sing it at Principality Stadium, a wedding, a funeral, or the kitchen on a Sunday.',
  l4Src: `${MEDIA_BASE}/welsh/anthem/lesson4.mp3`,
  lessonsTip: "If you get bits wrong – and you will – keep going. The SSi method is designed around mistakes. Guess, try, miss, and we'll give it to you again. That's the whole trick.",

  songKicker: 'The lyrics',
  songTitle: 'All three verses and the chorus.',
  songSub: 'The course teaches you the first verse and the chorus. For completeness, here are all three verses and the chorus side by side – Welsh on the left, a plain English translation on the right.',
  songVerseLabel: 'Verse',
  songTipsTitle: 'Pronunciation notes',
  songTipsBody: "The 'dd' in Nhadau is the English 'th' in 'the'. The 'ch' in 'bach' is the sound in Scottish 'loch'. 'Nh' in Nhadau is a mutation of 'T' (tadau → nhadau after 'fy') – Welsh does this all the time, and you get used to it faster than you'd think.",

  historyKicker: 'How it came about',
  historyTitle: 'How Wales introduced national anthems to the world of sport.',
  historyIntro: "One January morning in 1856, a twenty-four-year-old harpist called James James left his father's woollen factory on Mill Street, Pontypridd, and took a contemplative walk along the banks of the river Rhondda. Whether he was humming or whistling is unknown – we'll leave that to your imagination – but the tune that came to him on that walk would become, half a century later, the first national anthem ever sung before an international sports fixture.",
  historyP1Heading: 'A son, a father, a harp.',
  historyP1Body: 'James was the son of a poet, Evan James, and showing promise as a poet himself. When he got home he said to his father: I have composed a melody, very fitting for a Welsh patriotic song – will you write some verses? Evan listened, said "fetch your harp, James", and took down the slate that always hung beside his armchair. In a few minutes the first verse was written. The song was called Glan Rhondda – the banks of the Rhondda.',
  historyP2Heading: '"Remember King David playing his harp."',
  historyP2Body: 'James\'s mother returned from evening service at Carmel Baptist Church to find her son playing the harp on the Sabbath, and reprimanded him severely. He replied, "Mother, remember King David playing his harp." Verses 2 and 3 were written the next day. The song was first performed in the vestry of Capel Tabor in Maesteg in early 1856, by a sixteen-year-old singer called Elizabeth John from Pontypridd.',
  historyP3Heading: 'From eisteddfod to stadium.',
  historyP3Body: 'At the Llangollen Eisteddfod in 1858, Thomas Llewelyn of Aberdare won a competition with a collection of Welsh airs that included Glan Rhondda. The adjudicator, Owain Alaw, asked to include it in his book Gems of Welsh Melody – and it was there that Glan Rhondda became Hen Wlad Fy Nhadau. Then, on 16 December 1905, Wales played New Zealand at Cardiff Arms Park in front of 40,000 people. The All Blacks performed the Haka. When it finished, Welsh player Teddy Morgan led his team in singing Hen Wlad Fy Nhadau, the crowd picked up the chorus, and the stadium rang out with the first national anthem ever sung at an international sports match. Wales won 3–0.',
  historyImg1Caption: 'The banks of a river in the Rhondda valley – where James James took his walk, January 1856.',
  historyImg2Caption: 'The harp – the instrument on which the anthem was first played, in a factory house in Pontypridd.',
  historyImg3Caption: 'Caerphilly Castle. One of the ancient Welsh landmarks the anthem\'s "old land of my fathers" still stands in view of.',

  bandKicker: 'What others are saying',
  bandTitle: "A small encouragement, from people who've been here.",
  bandQ1: "SSi is an excellent way to build confidence. It gets you speaking and really works! I'm very grateful to them.",
  bandN1: 'Leanne Wood',
  bandR1: 'Former Leader of Plaid Cymru · SSi Welsh',
  bandPhoto1: 'leanne-wood.jpg',
  bandQ2: "I'm on a four-month trip to Central America and about 95% of the Spanish I'm using here came from SSi.",
  bandN2: 'Rachel Healy',
  bandR2: 'Learner · SSi Spanish · Costa Rica',
  bandPhoto2: 'rachel-healy.png',
  bandQ3: 'The SSi approach flies in the face of established language learning theory and method – and yet, it works.',
  bandN3: 'Prof. Tess Fitzpatrick',
  bandR3: 'Head of Applied Linguistics · Swansea University',
  bandPhoto3: 'tess-fitzpatrick.png',

  rTakeKicker: 'Loved the lessons?',
  rTakeTitle: "That's the method. And there's a whole course of it.",
  rTakeBody: 'Those four lessons are a real taste of how SSi works. If it clicked, the full course will take you from complete beginner to your first Welsh conversation in about six months – no grammar tables, no written homework, no stress.',
  rTakeCta1: 'See pricing',
  rTakeCta2: 'Try a free sample',
  rTakeQuote: "The reaction was nothing like my response to 'Mae Hen Wlad fy Nhadau' from the very first time I heard it. It had passion and rhythm and it was deeply moving to hear those words sung with such fervour.",
  rTakeQuoteName: 'Nick W',
  rTakeQuoteRole: 'SSi Welsh learner',

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero', keys: ['rCrumb','rKicker','rTitle','rLede'], long:['rLede'] },
  { name: 'Promises', keys: ['promiseKicker','promiseTitle','promise1Title','promise1Body','promise2Title','promise2Body','promise3Title','promise3Body'], long:['promise1Body','promise2Body','promise3Body'] },
  { name: 'Lessons', keys: ['lessonsKicker','lessonsTitle','lessonsLede','l1Num','l1Badge','l1Title','l1Body','l1Src','l2Num','l2Badge','l2Title','l2Body','l2Src','l3Num','l3Badge','l3Title','l3Body','l3Src','l4Num','l4Badge','l4Title','l4Body','l4Src','lessonsTip'], long:['lessonsLede','l1Body','l2Body','l3Body','l4Body','lessonsTip'] },
  { name: 'Lyrics', keys: ['songKicker','songTitle','songSub','songVerseLabel','songTipsTitle','songTipsBody'], long:['songSub','songTipsBody'] },
  { name: 'History', keys: ['historyKicker','historyTitle','historyIntro','historyP1Heading','historyP1Body','historyP2Heading','historyP2Body','historyP3Heading','historyP3Body','historyImg1Caption','historyImg2Caption','historyImg3Caption'], long:['historyIntro','historyP1Body','historyP2Body','historyP3Body'] },
  { name: 'Quote band', keys: ['bandKicker','bandTitle','bandQ1','bandN1','bandR1','bandQ2','bandN2','bandR2','bandQ3','bandN3','bandR3'], long:['bandQ1','bandQ2','bandQ3'] },
  { name: 'Take it further', keys: ['rTakeKicker','rTakeTitle','rTakeBody','rTakeCta1','rTakeCta2','rTakeQuote','rTakeQuoteName','rTakeQuoteRole'], long:['rTakeBody','rTakeQuote'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

const VERSES = [
  {
    cy: [
      'Mae hen wlad fy nhadau yn annwyl i mi,',
      'Gwlad beirdd a chantorion, enwogion o fri;',
      'Ei gwrol ryfelwyr, gwladgarwyr tra mâd,',
      'Dros ryddid gollasant eu gwaed.',
      '',
      "Gwlad! Gwlad! Pleidiol wyf i'm gwlad,",
      "Tra môr yn fur i'r bur hoff bau,",
      "O bydded i'r heniaith barhau.",
    ],
    en: [
      'The old land of my fathers is dear to me,',
      'Land of bards and singers, famous men of renown;',
      'Her brave warriors, great patriots,',
      'Shed their blood for freedom.',
      '',
      "Country! Country! I'm devoted to my country,",
      'While the sea is a wall to the pure, loved land,',
      'May the old language endure.',
    ],
  },
  {
    cy: [
      'Hen Gymru fynyddig, paradwys y bardd;',
      "Pob dyffryn, pob clogwyn, i'm golwg sydd hardd.",
      'Trwy deimlad gwladgarol, mor swynol yw si',
      'Ei nentydd, afonydd, i mi.',
    ],
    en: [
      'Old mountainous Wales, paradise of the bard;',
      'Every valley, every cliff, is beautiful in my sight.',
      'Through patriotic feeling, how enchanting is the murmur',
      'Of her brooks and rivers, to me.',
    ],
  },
  {
    cy: [
      'Os treisiodd y gelyn fy ngwlad tan ei droed,',
      'Mae hen iaith y Cymry mor fyw ag erioed,',
      'Ni luddiwyd yr awen gan erchyll law brad,',
      'Na thelyn berseiniol fy ngwlad.',
    ],
    en: [
      'Though the enemy has trampled my country underfoot,',
      'The old language of the Welsh is as alive as ever,',
      "The muse has not been silenced by treason's cruel hand,",
      'Nor the tuneful harp of my country.',
    ],
  },
];

const CHIPS = [
  { key:'Format',   val:'Mini course · 4 lessons' },
  { key:'Level',    val:'Complete beginners' },
  { key:'Written',  val:'1856 · Pontypridd' },
  { key:'Cost',     val:'Free, always' },
];

/* --- Promise band --- */
const pr = {
  wrap: { padding:'64px 32px 20px', maxWidth:1080, margin:'0 auto' },
  head: { marginBottom:32, maxWidth:640 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.2, margin:0, letterSpacing:'-.01em', textWrap:'balance' },
  grid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:24 },
  card: { padding:'26px 28px', background:'var(--offWhite, #FDF5F5)', borderRadius:16, border:'1px solid var(--border)' },
  num: { fontFamily:'var(--font-display)', fontSize:22, color:'var(--brandRed)', lineHeight:1, marginBottom:14 },
  cardTitle: { fontFamily:'var(--font-display)', fontSize:20, margin:'0 0 10px', letterSpacing:'-.005em' },
  cardBody: { fontSize:15, lineHeight:1.6, color:'var(--fg-1)', margin:0, textWrap:'pretty' },
};
function PromiseBand({ t }) {
  const items = [
    { n:'01', title:t.promise1Title, body:t.promise1Body },
    { n:'02', title:t.promise2Title, body:t.promise2Body },
    { n:'03', title:t.promise3Title, body:t.promise3Body },
  ];
  return (
    <section style={pr.wrap}>
      <header style={pr.head}>
        <div style={pr.kicker}>{t.promiseKicker}</div>
        <h2 style={pr.h2}>{t.promiseTitle}</h2>
      </header>
      <div className="ssi-res__promiseGrid" style={pr.grid}>
        {items.map((it, i) => (
          <article key={i} style={pr.card}>
            <div style={pr.num}>{it.n}</div>
            <h3 style={pr.cardTitle}>{it.title}</h3>
            <p style={pr.cardBody}>{it.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* --- Lesson cards (four audio blocks, 2x2 grid) --- */
const ls = {
  wrap: { padding:'56px 32px 32px', maxWidth:1080, margin:'0 auto' },
  head: { marginBottom:32, maxWidth:640 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.2, margin:'0 0 10px', letterSpacing:'-.01em' },
  lede: { fontSize:15.5, color:'var(--grey)', margin:0, maxWidth:'62ch', lineHeight:1.6, textWrap:'pretty' },
  grid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 },
  card: { background:'#fff', border:'1px solid var(--border)', borderRadius:18, padding:'26px 28px 26px', display:'grid', gridTemplateColumns:'auto 1fr', gap:20, alignItems:'start' },
  num: { fontFamily:'var(--font-display)', fontSize:48, color:'var(--brandRed)', lineHeight:.9, letterSpacing:'-.02em', minWidth:64 },
  body: { minWidth:0 },
  meta: { display:'flex', alignItems:'center', gap:10, marginBottom:6 },
  chip: { fontSize:11, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, border:'1px solid var(--brandRed)', padding:'2px 9px', borderRadius:999 },
  title: { fontFamily:'var(--font-display)', fontSize:20, margin:'0 0 8px', letterSpacing:'-.005em' },
  text: { fontSize:14.5, color:'var(--grey)', lineHeight:1.55, margin:'0 0 16px', textWrap:'pretty' },
  audio: { width:'100%' },
  tip: { marginTop:20, padding:'20px 24px', background:'var(--offWhite, #FDF5F5)', border:'1px dashed var(--border-strong)', borderRadius:14, fontSize:14.5, lineHeight:1.6, color:'var(--fg-1)', textWrap:'pretty' },
  tipLabel: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginRight:10 },
};
function Lessons({ t }) {
  const lessons = [
    {n:t.l1Num, badge:t.l1Badge, title:t.l1Title, body:t.l1Body, src:t.l1Src},
    {n:t.l2Num, badge:t.l2Badge, title:t.l2Title, body:t.l2Body, src:t.l2Src},
    {n:t.l3Num, badge:t.l3Badge, title:t.l3Title, body:t.l3Body, src:t.l3Src},
    {n:t.l4Num, badge:t.l4Badge, title:t.l4Title, body:t.l4Body, src:t.l4Src},
  ];
  return (
    <section style={ls.wrap}>
      <header style={ls.head}>
        <div style={ls.kicker}>{t.lessonsKicker}</div>
        <h2 style={ls.h2}>{t.lessonsTitle}</h2>
        <p style={ls.lede}>{t.lessonsLede}</p>
      </header>
      <div className="ssi-res__lessonsGrid" style={ls.grid}>
        {lessons.map((l, i) => (
          <article key={i} style={ls.card}>
            <div style={ls.num}>{l.n}</div>
            <div style={ls.body}>
              <div style={ls.meta}>
                <span style={ls.chip}>{l.badge}</span>
              </div>
              <h3 style={ls.title}>{l.title}</h3>
              <p style={ls.text}>{l.body}</p>
              <audio controls preload="metadata" style={ls.audio} src={l.src}>
                Your browser doesn't support audio – <a href={l.src}>download the MP3</a>.
              </audio>
            </div>
          </article>
        ))}
      </div>
      <div style={ls.tip}>
        <span style={ls.tipLabel}>A tiny bit of advice</span>
        {t.lessonsTip}
      </div>
    </section>
  );
}

/* --- History section --- */
const hi = {
  wrap: { background:'var(--offWhite, #FDF5F5)', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', padding:'80px 32px', marginTop:48 },
  inner: { maxWidth:1080, margin:'0 auto' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { fontFamily:'var(--font-display)', fontSize:40, lineHeight:1.1, margin:'0 0 22px', letterSpacing:'-.02em', textWrap:'balance', maxWidth:'24ch' },
  intro: { fontSize:18, lineHeight:1.6, color:'var(--fg-1)', margin:'0 0 56px', maxWidth:'62ch', textWrap:'pretty' },
  body: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:48, alignItems:'start' },
  col: { display:'flex', flexDirection:'column', gap:40 },
  p: {},
  pHeading: { fontFamily:'var(--font-display)', fontSize:22, margin:'0 0 12px', letterSpacing:'-.005em' },
  pBody: { fontSize:16, lineHeight:1.7, color:'var(--fg-1)', margin:0, textWrap:'pretty' },
  imgWrap: { background:'#fff', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' },
  img: { aspectRatio:'4/5', width:'100%', display:'block', objectFit:'cover' },
  caption: { fontSize:13, color:'var(--grey)', padding:'12px 16px', lineHeight:1.5, borderTop:'1px solid var(--border)' },
};
function HistoryImage({ src, alt, caption }) {
  return (
    <figure style={{margin:0, ...hi.imgWrap}}>
      <img src={src} alt={alt} style={hi.img} loading="lazy"/>
      {caption && <figcaption style={hi.caption}>{caption}</figcaption>}
    </figure>
  );
}
function History({ t }) {
  return (
    <section style={hi.wrap}>
      <div style={hi.inner}>
        <div style={hi.kicker}>{t.historyKicker}</div>
        <h2 style={hi.h2}>{t.historyTitle}</h2>
        <p style={hi.intro}>{t.historyIntro}</p>
        <div className="ssi-res__historyBody" style={hi.body}>
          <div style={hi.col}>
            <div style={hi.p}>
              <h3 style={hi.pHeading}>{t.historyP1Heading}</h3>
              <p style={hi.pBody}>{t.historyP1Body}</p>
            </div>
            <HistoryImage src="/assets/anthem/harp.jpg" alt="Close-up of harp strings" caption={t.historyImg2Caption}/>
            <div style={hi.p}>
              <h3 style={hi.pHeading}>{t.historyP3Heading}</h3>
              <p style={hi.pBody}>{t.historyP3Body}</p>
            </div>
          </div>
          <div style={hi.col}>
            <HistoryImage src="/assets/anthem/river.jpg" alt="A quiet river or canal with trees along both banks" caption={t.historyImg1Caption}/>
            <div style={hi.p}>
              <h3 style={hi.pHeading}>{t.historyP2Heading}</h3>
              <p style={hi.pBody}>{t.historyP2Body}</p>
            </div>
            <HistoryImage src="/assets/anthem/castle.jpg" alt="Caerphilly Castle under a blue sky" caption={t.historyImg3Caption}/>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function WelshAnthem() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Welsh national anthem">
      <Header t={tweaks}/>
      <main>
        <RHero t={tweaks} chips={CHIPS}/>
        <PromiseBand t={tweaks}/>
        <Lessons t={tweaks}/>
        <LyricSong t={tweaks} verses={VERSES} hideAudio={true}/>
        <History t={tweaks}/>
        <QuoteBand t={tweaks}/>
        <RTakeItFurther t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
